'use client';

import { MixerHorizontalIcon } from '@radix-ui/react-icons';
import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@noirproperties/ui/components/ui/sheet';
import { Button } from '@noirproperties/ui/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@noirproperties/ui/components/ui/popover';
import { Input } from '@noirproperties/ui/components/ui/input';
import { ArrowUp, X, Bed, Bath, MapPin, Euro, Maximize2 } from 'lucide-react';
import { ScrollArea } from '@noirproperties/ui/components/ui/scroll-area';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@noirproperties/ui/components/ui/toggle-group';
import { normalizeString } from '@noirproperties/string-utils/utils';
import { Label } from '@noirproperties/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noirproperties/ui/components/ui/select';
import { FaSwimmingPool as SwimmingPool } from 'react-icons/fa';
import { useDebounce } from 'use-debounce';
import { cn } from '@/lib/utils';
import {
  useBathroomsQueryParam,
  useBedroomsQueryParam,
  useCoastQueryParam,
  usePoolQueryParam,
  useMinPriceQueryParam,
  useMaxPriceQueryParam,
  useProvinceQueryParam,
  useQueryQueryParam,
  useSizeQueryParam,
  useOrderByQueryParam,
} from '@/components/client/properties-list.client';
import { useCurrencyFormatter } from '@/lib/i18n/client';
import { Trans, Plural, useLingui } from '@lingui/react/macro';

type FilterToolbarProps = {
  readonly priceSteps: readonly {
    readonly label: string;
    readonly value: number;
  }[];
  readonly coasts: readonly {
    readonly label: string;
    readonly value: string;
  }[];
  readonly provinces: readonly {
    readonly label: string;
    readonly value: string;
  }[];
  active?: boolean;
  lang: string;
};

// TODO: Needs to filter provinces based on coast, etc
export default function FilterToolbar({
  priceSteps,
  coasts,
  provinces,
  active = false,
  lang,
}: FilterToolbarProps) {
  const [queryQueryParam, setQueryQueryParam] = useQueryQueryParam();
  const [minPriceQueryParam, setMinPriceQueryParam] = useMinPriceQueryParam();
  const [maxPriceQueryParam, setMaxPriceQueryParam] = useMaxPriceQueryParam();
  const [coastQueryParam, setCoastQueryParam] = useCoastQueryParam();
  const [provinceQueryParam, setProvinceQueryParam] = useProvinceQueryParam();
  const [poolQueryParam, setPoolQueryParam] = usePoolQueryParam();
  const [bedroomsQueryParam, setBedroomsQueryParam] = useBedroomsQueryParam();
  const [bathroomsQueryParam, setBathroomsQueryParam] =
    useBathroomsQueryParam();
  const [sizeQueryParam, setSizeQueryParam] = useSizeQueryParam();
  const [orderByQueryParam, setOrderByQueryParam] = useOrderByQueryParam();

  const [inputValue, setInputValue] = React.useState(queryQueryParam || '');

  const { t } = useLingui();

  // Use the useDebounce hook to debounce the input value
  const [debouncedInputValue] = useDebounce(inputValue, 300);

  // Use an effect to update the query param when the debounced value changes
  React.useEffect(() => {
    void setQueryQueryParam(debouncedInputValue || null);
  }, [debouncedInputValue, setQueryQueryParam]);

  const onReset = React.useCallback(() => {
    setInputValue('');
    void setQueryQueryParam(null);
    void setMinPriceQueryParam(null);
    void setMaxPriceQueryParam(null);
    void setCoastQueryParam(null);
    void setProvinceQueryParam(null);
    void setBedroomsQueryParam(null);
    void setBathroomsQueryParam(null);
    void setSizeQueryParam(null);
    void setPoolQueryParam(null);
    void setOrderByQueryParam(null);
  }, [
    setBathroomsQueryParam,
    setBedroomsQueryParam,
    setCoastQueryParam,
    setPoolQueryParam,
    setMinPriceQueryParam,
    setMaxPriceQueryParam,
    setProvinceQueryParam,
    setQueryQueryParam,
    setSizeQueryParam,
    setOrderByQueryParam,
  ]);

  const currencyFormatter = useCurrencyFormatter(lang);

  const [openPopover, setOpenPopover] = React.useState<string | null>(null);

  const handlePopoverOpen = (key: string) => {
    setOpenPopover(key);
  };

  const handlePopoverClose = () => {
    setOpenPopover(null);
  };

  const handleValueChange = (key: string, value: string | number | boolean) => {
    switch (key) {
      case 'minPrice':
        if (value === 'any') {
          void setMinPriceQueryParam(null);
        } else {
          void setMinPriceQueryParam(Number(value));
        }
        break;
      case 'maxPrice':
        if (value === 'any') {
          void setMaxPriceQueryParam(null);
        } else {
          void setMaxPriceQueryParam(Number(value));
        }
        break;
      case 'coast':
        if (value === 'any') {
          void setCoastQueryParam(null);
        } else {
          void setCoastQueryParam(value as string);
        }
        break;
      case 'province':
        if (value === 'any') {
          void setProvinceQueryParam(null);
        } else {
          void setProvinceQueryParam(value as string);
        }
        break;
      case 'size':
        if (value === 'any') {
          void setSizeQueryParam(null);
        } else {
          void setSizeQueryParam(Number(value));
        }
        break;
      case 'bedrooms':
        if (value === 'any') {
          void setBedroomsQueryParam(null);
        } else if (value === '6+') {
          void setBedroomsQueryParam(6);
        } else {
          void setBedroomsQueryParam(Number(value));
        }
        break;
      case 'bathrooms':
        if (value === 'any') {
          void setBathroomsQueryParam(null);
        } else if (value === '6+') {
          void setBathroomsQueryParam(6);
        } else {
          void setBathroomsQueryParam(Number(value));
        }
        break;
      case 'pool':
        if (value === '' || value === 'any') {
          void setPoolQueryParam(null);
          break;
        }
        void setPoolQueryParam(value === 'true');
        break;
      case 'orderBy':
        if (value === 'any' || value === '') {
          void setOrderByQueryParam(null);
        } else {
          void setOrderByQueryParam(value as 'price_asc' | 'price_desc');
        }
        break;
    }
    handlePopoverClose();
  };

  // Add these functions to normalize and find matching options
  const findMatchingCoast = (value: string | null) => {
    if (!value) return null;
    const normalizedValue = normalizeString(value).toLowerCase();
    return coasts.find(
      (coast) => normalizeString(coast.value).toLowerCase() === normalizedValue,
    );
  };

  const findMatchingProvince = (value: string | null) => {
    if (!value) return null;
    const normalizedValue = normalizeString(value).toLowerCase();
    return provinces.find(
      (province) =>
        normalizeString(province.value).toLowerCase() === normalizedValue,
    );
  };

  // Use these functions when rendering the coast and province values
  const matchedCoast = findMatchingCoast(coastQueryParam);
  const matchedProvince = findMatchingProvince(provinceQueryParam);

  return (
    <div className="w-full">
      <Sheet>
        <div className="flex flex-col items-start gap-y-4 sm:flex-row sm:items-center sm:gap-x-6">
          <div className="flex w-full sm:w-96">
            <div className="flex-grow">
              <Input
                autoComplete="off"
                autoCorrect="off"
                className="rounded-r-none bg-white"
                onChange={(e) => {
                  setInputValue(e.target.value);
                }}
                placeholder={t({
                  context: 'searchbox placeholder',
                  message: `e.g. 'Villa in Alicante'`,
                })}
                spellCheck="false"
                value={inputValue}
              />
            </div>
            <Button
              className="flex-shrink-0 rounded-l-none shadow-sm"
              size="icon"
              type="submit"
            >
              <ArrowUp />
            </Button>
          </div>
          {/* Group Sort and Filter controls */}
          <div className="flex items-center gap-x-4">
            {/* Sorting Dropdown with Label */}
            <div className="flex items-center gap-x-2">
              <Label
                htmlFor="sort-order"
                className="text-sm font-medium text-gray-700"
              >
                <Trans>Sort by:</Trans>
              </Label>
              <Select
                onValueChange={(value) => {
                  handleValueChange('orderBy', value);
                }}
                value={orderByQueryParam || 'any'}
              >
                <SelectTrigger
                  id="sort-order"
                  className="w-auto min-w-[160px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <SelectValue placeholder="Relevance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="any">
                      <Trans context="Sorting of search results">
                        Relevance
                      </Trans>
                    </SelectItem>
                    <SelectItem value="price_asc">
                      <Trans context="Sorting of search results">
                        Price (Low to High)
                      </Trans>
                    </SelectItem>
                    <SelectItem value="price_desc">
                      <Trans context="Sorting of search results">
                        Price (High to Low)
                      </Trans>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Filter Trigger */}
            <SheetTrigger>
              <div
                className={cn(
                  `flex items-center gap-x-2 ${
                    active ? 'rounded-md bg-gray-200 p-2' : 'text-gray-700'
                  }`,
                )}
              >
                <MixerHorizontalIcon />
                <p className="text-sm font-medium">
                  <Trans context="Property search filter">Filter</Trans>
                </p>
              </div>
            </SheetTrigger>
            {active ? (
              <Button
                className="text-sm font-medium text-gray-700"
                onClick={() => {
                  onReset();
                }}
                type="button"
                variant="ghost"
              >
                <Trans context="Property search filter">Clear</Trans>
              </Button>
            ) : null}
          </div>
        </div>
        <SheetContent
          className="flex flex-col bg-neutral-50 px-6"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
          side="right"
        >
          <SheetHeader>
            <SheetTitle>Filter</SheetTitle>
            <SheetDescription className="text-left">
              <Trans context="Property search filter">Narrow your search</Trans>
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="flex-grow">
            <div className="py-6">
              <div className="flex w-full flex-col gap-y-6">
                {/* Min Price filter */}
                <div className="space-y-2 px-2">
                  <Label className="flex items-center text-sm font-medium">
                    <Euro className="mr-2 h-4 w-4" />
                    <Trans context="Property search filter">Min Price</Trans>
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('minPrice', value);
                    }}
                    value={minPriceQueryParam?.toString() || 'any'}
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue placeholder="Select min price" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {minPriceQueryParam &&
                        !priceSteps.some(
                          (p) => p.value === minPriceQueryParam,
                        ) ? (
                          <SelectItem value={minPriceQueryParam.toString()}>
                            {currencyFormatter(minPriceQueryParam)}{' '}
                            <Trans context="Property search filter">
                              (Custom)
                            </Trans>
                          </SelectItem>
                        ) : null}
                        {priceSteps.map((price) => (
                          <SelectItem
                            key={price.value}
                            value={price.value.toString()}
                          >
                            {currencyFormatter(price.value)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {/* Max Price filter */}
                <div className="space-y-2 px-2">
                  <Label className="flex items-center text-sm font-medium">
                    <Euro className="mr-2 h-4 w-4" />
                    <Trans context="Property search filter">Max Price</Trans>
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('maxPrice', value);
                    }}
                    value={maxPriceQueryParam?.toString() || 'any'}
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue placeholder="Select max price" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {maxPriceQueryParam &&
                        !priceSteps.some(
                          (p) => p.value === maxPriceQueryParam,
                        ) ? (
                          <SelectItem value={maxPriceQueryParam.toString()}>
                            {currencyFormatter(maxPriceQueryParam)}{' '}
                            <Trans context="Property search filter">
                              (Custom)
                            </Trans>
                          </SelectItem>
                        ) : null}
                        {priceSteps.map((price) => (
                          <SelectItem
                            key={price.value}
                            value={price.value.toString()}
                          >
                            {currencyFormatter(price.value)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="h-px bg-gray-200" /> {/* Subtle separator */}
                {/* Location filters */}
                <div className="space-y-4 px-2">
                  <div className="space-y-2">
                    <Label className="flex items-center text-sm font-medium">
                      <MapPin className="mr-2 h-4 w-4" />
                      <Trans context="Property search filter">Location</Trans>
                    </Label>
                    <Select
                      onValueChange={(value) => {
                        handleValueChange('coast', value);
                      }}
                      value={coastQueryParam || 'any'}
                    >
                      <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                        <SelectValue placeholder="Select coast" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="any">
                            <Trans context="Property search filter">Any</Trans>
                          </SelectItem>
                          {coasts.map((coast) => (
                            <SelectItem key={coast.value} value={coast.value}>
                              {coast.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('province', value);
                    }}
                    value={provinceQueryParam || 'any'}
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {provinces.map((province) => (
                          <SelectItem
                            key={province.value}
                            value={province.value}
                          >
                            {province.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="h-px bg-gray-200" /> {/* Subtle separator */}
                {/* Bedrooms filter */}
                <div className="space-y-2 px-2">
                  <Label className="flex items-center text-sm font-medium">
                    <Bed className="mr-2 h-4 w-4" />
                    <Trans>Bedrooms</Trans>
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('bedrooms', value);
                    }}
                    value={
                      bedroomsQueryParam !== null
                        ? bedroomsQueryParam >= 6
                          ? '6+'
                          : bedroomsQueryParam.toString()
                        : 'any'
                    }
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue
                        placeholder={t({
                          context: 'Property search filter',
                          message: `Select bedrooms`,
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {[1, 2, 3, 4, 5, '6+'].map((option) => (
                          <SelectItem key={option} value={option.toString()}>
                            {typeof option === 'number' ? (
                              <Plural
                                value={option}
                                one="# bedroom"
                                other="# bedrooms"
                              />
                            ) : (
                              <Trans>6+ bedrooms</Trans>
                            )}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {/* Bathrooms filter */}
                <div className="space-y-2 px-2">
                  <Label className="flex items-center text-sm font-medium">
                    <Bath className="mr-2 h-4 w-4" />
                    <Trans>Bathrooms</Trans>
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('bathrooms', value);
                    }}
                    value={
                      bathroomsQueryParam !== null && bathroomsQueryParam >= 6
                        ? '6+'
                        : bathroomsQueryParam?.toString() || 'any'
                    }
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue placeholder="Select bathrooms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {[1, 2, 3, 4, 5, '6+'].map((option) => (
                          <SelectItem key={option} value={option.toString()}>
                            {typeof option === 'number' ? (
                              <Plural
                                value={option}
                                one="# bathroom"
                                other="# bathrooms"
                              />
                            ) : (
                              <Trans>6+ bathrooms</Trans>
                            )}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                {/* Living Area filter */}
                <div className="space-y-2 px-2">
                  <Label className="flex items-center text-sm font-medium">
                    <Maximize2 className="mr-2 h-4 w-4" />
                    <Trans context="Property search filter">Living Area</Trans>
                  </Label>
                  <Select
                    onValueChange={(value) => {
                      handleValueChange('size', value);
                    }}
                    value={sizeQueryParam?.toString() || 'any'}
                  >
                    <SelectTrigger className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <SelectValue placeholder="Select living area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="any">
                          <Trans context="Property search filter">Any</Trans>
                        </SelectItem>
                        {sizeQueryParam &&
                        ![50, 100, 150, 200, 250, 300].includes(
                          sizeQueryParam,
                        ) ? (
                          <SelectItem value={sizeQueryParam.toString()}>
                            {sizeQueryParam} m² (Custom)
                          </SelectItem>
                        ) : null}
                        {[50, 100, 150, 200, 250, 300].map((size) => (
                          <SelectItem key={size} value={size.toString()}>
                            {size} m²
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="h-px bg-gray-200" /> {/* Subtle separator */}
                {/* Pool filter */}
                <div className="space-y-2 px-2">
                  <Label className="text-sm font-medium">
                    <Trans context="Property search filter">Pool</Trans>
                  </Label>
                  <ToggleGroup
                    className="justify-between"
                    onValueChange={(value) => {
                      handleValueChange('pool', value);
                    }}
                    type="single"
                    value={
                      poolQueryParam === null
                        ? 'any'
                        : poolQueryParam.toString()
                    }
                  >
                    <ToggleGroupItem className="flex-1" value="any">
                      <span className="sr-only">
                        <Trans context="Property search filter">Any</Trans>
                      </span>
                      <Trans context="Property search filter">Any</Trans>
                    </ToggleGroupItem>
                    <ToggleGroupItem className="flex-1" value="true">
                      <SwimmingPool className="mr-2 h-4 w-4" />
                      <span className="sr-only">
                        <Trans context="Property search filter">
                          With Pool
                        </Trans>
                      </span>
                      <Trans>Yes</Trans>
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      className="flex-1 text-gray-500"
                      value="false"
                    >
                      <SwimmingPool className="mr-2 h-4 w-4 opacity-50" />
                      <span className="sr-only">
                        <Trans context="Property search filter">
                          Without Pool
                        </Trans>
                      </span>
                      <Trans>No</Trans>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                {/* Move Reset and Apply buttons here */}
                <div className="flex justify-end space-x-2 pt-6">
                  <Button
                    onClick={() => {
                      onReset();
                    }}
                    type="button"
                    variant="secondary"
                  >
                    <Trans context="Property search filter">Reset</Trans>
                  </Button>
                  <SheetClose asChild>
                    <Button className="bg-indigo-600" type="submit">
                      <Trans context="Property search filter">Apply</Trans>
                    </Button>
                  </SheetClose>
                </div>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {active ? (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
          {debouncedInputValue ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Property search filter">Search query</Trans>
              {': '}
              <span className="text-indigo-600">{debouncedInputValue}</span>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  setInputValue('');
                  void setQueryQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}

          {minPriceQueryParam || maxPriceQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              {minPriceQueryParam && maxPriceQueryParam ? (
                <>
                  <Trans context="Property search filter">Price Range</Trans>
                  {': '}
                </>
              ) : minPriceQueryParam ? (
                <>
                  <Trans context="Property search filter">Min Price</Trans>
                  {': '}
                </>
              ) : (
                <>
                  <Trans context="Property search filter">Max Price</Trans>
                  {': '}
                </>
              )}

              {minPriceQueryParam && (
                <>
                  <Popover
                    onOpenChange={(open) => {
                      open
                        ? handlePopoverOpen('minPrice')
                        : handlePopoverClose();
                    }}
                    open={openPopover === 'minPrice'}
                  >
                    <PopoverTrigger asChild>
                      <span className="cursor-pointer text-indigo-600 hover:underline">
                        {currencyFormatter(minPriceQueryParam)}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-0">
                      <ScrollArea className="h-48">
                        <div className="p-4">
                          {priceSteps.map((price) => (
                            <Button
                              className="w-full justify-start"
                              key={price.value}
                              onClick={() => {
                                handleValueChange('minPrice', price.value);
                              }}
                              variant="ghost"
                            >
                              {currencyFormatter(price.value)}
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                  <Button
                    className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                    onClick={() => {
                      void setMinPriceQueryParam(null);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              )}
              {minPriceQueryParam && maxPriceQueryParam ? ' - ' : ''}
              {maxPriceQueryParam && (
                <>
                  <Popover
                    onOpenChange={(open) => {
                      open
                        ? handlePopoverOpen('maxPrice')
                        : handlePopoverClose();
                    }}
                    open={openPopover === 'maxPrice'}
                  >
                    <PopoverTrigger asChild>
                      <span className="cursor-pointer text-indigo-600 hover:underline">
                        {currencyFormatter(maxPriceQueryParam)}
                      </span>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-0">
                      <ScrollArea className="h-48">
                        <div className="p-4">
                          {priceSteps.map((price) => (
                            <Button
                              className="w-full justify-start"
                              key={price.value}
                              onClick={() => {
                                handleValueChange('maxPrice', price.value);
                              }}
                              variant="ghost"
                            >
                              {currencyFormatter(price.value)}
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                  <Button
                    className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                    onClick={() => {
                      void setMaxPriceQueryParam(null);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </>
              )}
            </p>
          ) : null}

          {coastQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Property search filter">Coast</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('coast') : handlePopoverClose();
                }}
                open={openPopover === 'coast'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {matchedCoast ? matchedCoast.label : coastQueryParam}
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-48">
                    <div className="p-4">
                      {coasts.map((coast) => (
                        <Button
                          className="w-full justify-start"
                          key={coast.value}
                          onClick={() => {
                            handleValueChange('coast', coast.value);
                          }}
                          variant="ghost"
                        >
                          {coast.label}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  void setCoastQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
          {provinceQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Property search filter">Province</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('province') : handlePopoverClose();
                }}
                open={openPopover === 'province'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {matchedProvince
                      ? matchedProvince.label
                      : provinceQueryParam}
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-48">
                    <div className="p-4">
                      {provinces.map((province) => (
                        <Button
                          className="w-full justify-start"
                          key={province.value}
                          onClick={() => {
                            handleValueChange('province', province.value);
                          }}
                          variant="ghost"
                        >
                          {province.label}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  void setProvinceQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
          {sizeQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Property search filter">Living Area</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('size') : handlePopoverClose();
                }}
                open={openPopover === 'size'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {sizeQueryParam} m²
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-48">
                    <div className="p-4">
                      {[50, 100, 150, 200, 250, 300].map((size) => (
                        <Button
                          className="w-full justify-start"
                          key={size}
                          onClick={() => {
                            handleValueChange('size', size);
                          }}
                          variant="ghost"
                        >
                          {size} m²
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  void setSizeQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
          {bedroomsQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans>Bedrooms</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('bedrooms') : handlePopoverClose();
                }}
                open={openPopover === 'bedrooms'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {bedroomsQueryParam === 6 ? '6+' : bedroomsQueryParam}
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-48">
                    <div className="p-4">
                      {[1, 2, 3, 4, 5, '6+'].map((option) => (
                        <Button
                          className="w-full justify-start"
                          key={option}
                          onClick={() => {
                            handleValueChange('bedrooms', option);
                          }}
                          variant="ghost"
                        >
                          {typeof option === 'number' ? (
                            <Plural
                              value={option}
                              one="# bedroom"
                              other="# bedrooms"
                            />
                          ) : (
                            <Trans>6+ bedrooms</Trans>
                          )}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  void setBedroomsQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
          {bathroomsQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans>Bathrooms</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('bathrooms') : handlePopoverClose();
                }}
                open={openPopover === 'bathrooms'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {bathroomsQueryParam >= 6 ? '6+' : bathroomsQueryParam}
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-48">
                    <div className="p-4">
                      {[1, 2, 3, 4, 5, '6+'].map((option) => (
                        <Button
                          className="w-full justify-start"
                          key={option}
                          onClick={() => {
                            handleValueChange('bathrooms', option);
                          }}
                          variant="ghost"
                        >
                          {typeof option === 'number' ? (
                            <Plural
                              value={option}
                              one="# bathroom"
                              other="# bathrooms"
                            />
                          ) : (
                            <Trans>6+ bathrooms</Trans>
                          )}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => {
                  void setBathroomsQueryParam(null);
                }}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
          {poolQueryParam !== null && (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Property search filter">Pool</Trans>
              {': '}
              <Popover
                onOpenChange={(open) => {
                  open ? handlePopoverOpen('pool') : handlePopoverClose();
                }}
                open={openPopover === 'pool'}
              >
                <PopoverTrigger asChild>
                  <span className="cursor-pointer text-indigo-600 hover:underline">
                    {poolQueryParam ? 'Yes' : 'No'}
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <ScrollArea className="h-24">
                    <div className="p-4">
                      <Button
                        className="w-full justify-start"
                        onClick={() => {
                          handleValueChange('pool', true);
                        }}
                        variant="ghost"
                      >
                        Yes
                      </Button>
                      <Button
                        className="w-full justify-start"
                        onClick={() => {
                          handleValueChange('pool', false);
                        }}
                        variant="ghost"
                      >
                        No
                      </Button>
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => void setPoolQueryParam(null)}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          )}
          {orderByQueryParam ? (
            <p className="text-sm font-bold tracking-tight text-gray-900 sm:text-base md:text-lg">
              <Trans context="Active filter label">Sorted by</Trans>
              {': '}
              <span className="text-indigo-600">
                {orderByQueryParam === 'price_asc' ? (
                  <Trans context="Sorting of search results">
                    Price (Low to High)
                  </Trans>
                ) : (
                  <Trans context="Sorting of search results">
                    Price (High to Low)
                  </Trans>
                )}
              </span>
              <Button
                className="ml-1 h-auto p-0 text-gray-500 hover:bg-transparent"
                onClick={() => void setOrderByQueryParam(null)}
                size="sm"
                variant="ghost"
              >
                <X className="h-3 w-3" />
              </Button>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
